import styled from '@emotion/styled';
import type { SortDirection } from '@tanstack/react-table';
import type { LegacyColumn, LegacyColumnDef } from '@tanstack/react-table/legacy';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import { format } from 'date-fns';
import type { ComponentProps, FunctionComponent, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { Fragment, memo, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useTranslate } from '~/shared/context/use-translate';
import type {
    JobArchitectureItemReference,
    JobArchitectureSkill,
    SkillCatalogTaskReference,
    SkillSyncStatus,
    SkillType,
} from '~/shared/data-contract/skill';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconDelete, IconHorizontalDots, IconSortArrow } from '~/ui/core/icons';
import {
    type TableConfigurableColumnDefinition,
    TableConfigureColumnsHeaderCell,
} from '~/ui/core/table/TableConfigureColumns';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { createSkillDetailTilePayload } from '../../skills';
import { useUrlTiles } from '../../url-tiles';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { SyncStatusBadge, SyncStatusOptions } from './sync-status-options';

const columnHelper = legacyCreateColumnHelper<JobArchitectureSkill>();

export const createCheckboxColumn = (): LegacyColumnDef<JobArchitectureSkill> =>
    columnHelper.display({
        id: 'checkbox',
        cell: ({ row }) => (
            <Checkbox
                aria-label={row.original.title}
                checked={row.getIsSelected()}
                onCheckedChange={(checked) => row.toggleSelected(checked === true)}
                onClick={(event) => event.stopPropagation()}
            />
        ),
        enableHiding: false,
        header: ({ table }) => {
            const { translate } = useContext(ApplicationContext);

            return (
                <Checkbox
                    aria-label={translate('common.select-all')}
                    checked={table.getIsAllRowsSelected()}
                    onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
                />
            );
        },
    });

export const HIDEABLE_COLUMNS: readonly TableConfigurableColumnDefinition<TranslationKey>[] = [
    { id: 'skillType', labelKey: 'pages.job-architecture-v2.skills.table-headers.skill-type' },
    { id: 'source', labelKey: 'pages.job-architecture-v2.skills.table-headers.source' },
    { id: 'jobProfiles', labelKey: 'pages.job-architecture-v2.skills.table-headers.job-architectures' },
    { id: 'roles', labelKey: 'pages.job-architecture-v2.skills.table-headers.job-roles' },
    { id: 'levels', labelKey: 'pages.job-architecture-v2.skills.table-headers.job-levels' },
    { id: 'competencies', labelKey: 'pages.job-architecture-v2.skills.table-headers.competencies' },
    { id: 'tasks', labelKey: 'pages.job-architecture-v2.skills.table-headers.tasks' },
    { id: 'learningOpportunities', labelKey: 'pages.job-architecture-v2.skills.table-headers.courses' },
    { id: 'users', labelKey: 'pages.job-architecture-v2.skills.table-headers.employees' },
    { id: 'syncStatus', labelKey: 'pages.job-architecture-v2.skills.table-headers.sync-status' },
    { id: 'addedAt', labelKey: 'pages.job-architecture-v2.skills.table-headers.added-at' },
];

export const getHideableSkillCatalogColumns = (
    includeTasks: boolean,
): readonly TableConfigurableColumnDefinition<TranslationKey>[] =>
    includeTasks ? HIDEABLE_COLUMNS : HIDEABLE_COLUMNS.filter((column) => column.id !== 'tasks');

const TranslatedHeader: FunctionComponent<{
    readonly translationKey:
        | 'added-at'
        | 'competencies'
        | 'courses'
        | 'employees'
        | 'job-architectures'
        | 'job-levels'
        | 'job-roles'
        | 'name'
        | 'skill-type'
        | 'source'
        | 'sync-status'
        | 'tasks';
}> = memo(({ translationKey }) => {
    const { translate } = useContext(ApplicationContext);
    const text = translate(`pages.job-architecture-v2.skills.table-headers.${translationKey}`);

    return (
        <span className="overflow-hidden text-ellipsis whitespace-nowrap" title={text}>
            {text}
        </span>
    );
});

const SortIndicator: FunctionComponent<{ readonly direction: SortDirection | false }> = ({ direction }) => {
    if (direction === 'asc') {
        return <IconSortArrow size={14} />;
    }

    if (direction === 'desc') {
        return <IconSortArrow size={14} style={{ transform: 'rotate(180deg)' }} />;
    }

    return <IconSortArrow size={14} style={{ opacity: 0.3 }} />;
};

export const SortableHeaderButton: FunctionComponent<{
    readonly direction: SortDirection | false;
    readonly label: string;
    readonly onClick: ComponentProps<'button'>['onClick'];
}> = ({ direction, label, onClick }) => (
    <button
        className="flex cursor-pointer select-none items-center gap-2 overflow-hidden border-0 bg-transparent p-0 text-left [font:inherit] text-inherit"
        title={label}
        type="button"
        onClick={onClick}
    >
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
        <SortIndicator direction={direction} />
    </button>
);

const SortableTranslatedHeader: FunctionComponent<{
    readonly column: LegacyColumn<JobArchitectureSkill>;
    readonly translationKey: 'added-at' | 'job-levels' | 'job-roles';
}> = ({ column, translationKey }) => {
    const translate = useTranslate();

    return (
        <SortableHeaderButton
            direction={column.getIsSorted()}
            label={translate(`pages.job-architecture-v2.skills.table-headers.${translationKey}`)}
            onClick={column.getToggleSortingHandler()}
        />
    );
};

export const createNameColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('title', {
        id: 'title',
        cell: ({ getValue, row }) => {
            const { openTile } = useUrlTiles();
            const contentLanguage = useJobArchitectureContentLanguage();

            return (
                <SkillBadge
                    skill={{
                        title: getValue(),
                        type: row.original.skillType,
                    }}
                    onClick={() =>
                        openTile(
                            createSkillDetailTilePayload({
                                preferredLanguage: contentLanguage,
                                skillId: row.original.id,
                            }),
                        )
                    }
                />
            );
        },
        enableHiding: false,
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="name" />,
    });
};

const TranslatedSkillType: FunctionComponent<{ readonly skillType: SkillType }> = memo(({ skillType }) => {
    const { translate } = useContext(ApplicationContext);

    return translate(`common.enums.skillType.${skillType}`);
});

const SkillTypeHeader: FunctionComponent = memo(() => {
    const { translate } = useContext(ApplicationContext);
    const text = translate('pages.job-architecture-v2.skills.table-headers.skill-type');

    return (
        <span className="overflow-hidden text-ellipsis whitespace-nowrap" title={text}>
            {text}
        </span>
    );
});

export const createSkillTypeColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('skillType', {
        id: 'skillType',
        cell: ({ getValue }) => {
            return <TranslatedSkillType skillType={getValue()} />;
        },
        enableSorting: false,
        header: () => <SkillTypeHeader />,
    });
};

const EmployeesHeader: FunctionComponent = memo(() => {
    const { translate } = useContext(ApplicationContext);
    const text = translate('pages.job-architecture-v2.skills.table-headers.employees');

    return (
        <span className="overflow-hidden text-ellipsis whitespace-nowrap" title={text}>
            {text}
        </span>
    );
});

export const createUsersColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('usersWithThisSkill', {
        id: 'users',
        cell: ({ getValue }) => {
            return <Fragment>{getValue()}</Fragment>;
        },
        enableSorting: false,
        header: () => <EmployeesHeader />,
    });
};

type SkillSource = 'attributed' | 'manual';

const getSkillSource = (skill: JobArchitectureSkill): SkillSource => {
    const hasJobArchitectures = skill.jobArchitecturesWithSkill.length > 0;
    const hasCourses = skill.learningOpportunitiesWithSkill.length > 0;

    return hasJobArchitectures || hasCourses ? 'attributed' : 'manual';
};

const TranslatedSource: FunctionComponent<{ readonly source: SkillSource }> = memo(({ source }) => {
    const { translate } = useContext(ApplicationContext);

    return translate(`pages.job-architecture-v2.skills.source.${source}`);
});

export const createSourceColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.display({
        id: 'source',
        cell: ({ row }) => {
            const source = getSkillSource(row.original);

            return <TranslatedSource source={source} />;
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="source" />,
    });
};

type SourceItem = { readonly id: string; readonly title: string };

type ReferenceColumn = Extract<
    ComponentProps<typeof TranslatedHeader>['translationKey'],
    'competencies' | 'courses' | 'job-architectures' | 'job-levels' | 'job-roles' | 'tasks'
>;

const badgeTriggerClasses =
    'inline-flex min-w-0 max-w-full items-center rounded-full border-none bg-transparent p-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

const BadgeDropdownTrigger: FunctionComponent<PropsWithChildren<{ readonly accessibleName: string }>> = ({
    accessibleName,
    children,
}) => (
    <DropdownMenuTrigger>
        <button
            aria-label={accessibleName}
            className={badgeTriggerClasses}
            type="button"
            onClick={(e) => e.stopPropagation()}
        >
            {children}
        </button>
    </DropdownMenuTrigger>
);

const useReferenceCountLabel = (column: ReferenceColumn, count: number): string => {
    const translate = useTranslate();

    return translate(`pages.job-architecture-v2.skills.reference-count.${column}`, { count });
};

type CountBadgeDropdownProps = {
    readonly column: ReferenceColumn;
    readonly items: readonly SourceItem[];
    readonly onItemClick: (id: string) => void;
};

const CountBadgeDropdown: FunctionComponent<CountBadgeDropdownProps> = ({ column, items, onItemClick }) => {
    const count = items.length;
    const accessibleName = useReferenceCountLabel(column, count);

    if (count === 0) {
        return <Badge variant="gray">–</Badge>;
    }

    return (
        <DropdownMenu>
            <BadgeDropdownTrigger accessibleName={accessibleName}>
                <Badge interactive={true} variant="gray">
                    {count.toString()}
                </Badge>
            </BadgeDropdownTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                {items.map((item) => (
                    <DropdownMenuItem
                        key={item.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            onItemClick(item.id);
                        }}
                    >
                        {item.title}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

type JobArchitecturesColumnCallbacks = {
    readonly navigateToArchitecture: (id: string) => void;
};

export const createJobArchitecturesColumn = ({
    navigateToArchitecture,
}: JobArchitecturesColumnCallbacks): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('jobArchitecturesWithSkill', {
        id: 'jobProfiles',
        cell: ({ getValue }) => {
            return (
                <CountBadgeDropdown
                    column="job-architectures"
                    items={getValue()}
                    onItemClick={navigateToArchitecture}
                />
            );
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="job-architectures" />,
    });
};

type CoursesColumnCallbacks = {
    readonly navigateToLearningOpportunity: (id: string) => void;
};

export const createCoursesColumn = ({
    navigateToLearningOpportunity,
}: CoursesColumnCallbacks): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('learningOpportunitiesWithSkill', {
        id: 'learningOpportunities',
        cell: ({ getValue }) => {
            return (
                <CountBadgeDropdown column="courses" items={getValue()} onItemClick={navigateToLearningOpportunity} />
            );
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="courses" />,
    });
};

const EMPTY_CAPPED_LIST = { items: [], totalCount: 0 } as const;

type CappedItemsBadgeDropdownProps<T extends { readonly id: string; readonly title: string }> = {
    readonly column: ReferenceColumn;
    readonly getItemTitle?: (item: T) => string;
    readonly list: { readonly items: readonly T[]; readonly totalCount: number };
    readonly onItemClick?: (item: T) => void;
};

const CappedItemsBadgeDropdown = <T extends { readonly id: string; readonly title: string }>({
    column,
    getItemTitle,
    list,
    onItemClick,
}: CappedItemsBadgeDropdownProps<T>): ReactElement => {
    const translate = useTranslate();
    const { items, totalCount } = list;
    const accessibleName = useReferenceCountLabel(column, totalCount);

    if (totalCount === 0) {
        return <Badge variant="gray">–</Badge>;
    }

    const hiddenCount = totalCount - items.length;

    return (
        <DropdownMenu>
            <BadgeDropdownTrigger accessibleName={accessibleName}>
                <Badge interactive={true} variant="gray">
                    {totalCount.toString()}
                </Badge>
            </BadgeDropdownTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                {items.map((item) => {
                    const title = getItemTitle?.(item) ?? item.title;

                    return onItemClick === undefined ? (
                        <DropdownMenuItem
                            key={item.id}
                            className="cursor-default"
                            onClick={(e) => e.stopPropagation()}
                            onSelect={(event) => event.preventDefault()}
                        >
                            {title}
                        </DropdownMenuItem>
                    ) : (
                        <DropdownMenuItem
                            key={item.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                onItemClick(item);
                            }}
                        >
                            {title}
                        </DropdownMenuItem>
                    );
                })}
                {hiddenCount > 0 && (
                    <div className="px-m py-s text-body-xxs text-text-content-subtle">
                        {translate('pages.job-architecture-v2.skills.more-items-not-shown', { count: hiddenCount })}
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

type JobRolesLevelsColumnCallbacks = {
    readonly onItemClick: (item: JobArchitectureItemReference) => void;
};

export const createJobRolesColumn = ({
    onItemClick,
}: JobRolesLevelsColumnCallbacks): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('roles', {
        id: 'roles',
        cell: ({ getValue }) => {
            return <CappedItemsBadgeDropdown column="job-roles" list={getValue()} onItemClick={onItemClick} />;
        },
        enableSorting: true,
        header: ({ column }) => <SortableTranslatedHeader column={column} translationKey="job-roles" />,
        sortDescFirst: true,
    });
};

export const createJobLevelsColumn = ({
    onItemClick,
}: JobRolesLevelsColumnCallbacks): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('levels', {
        id: 'levels',
        cell: ({ getValue }) => {
            return <CappedItemsBadgeDropdown column="job-levels" list={getValue()} onItemClick={onItemClick} />;
        },
        enableSorting: true,
        header: ({ column }) => <SortableTranslatedHeader column={column} translationKey="job-levels" />,
        sortDescFirst: true,
    });
};

export const createCompetenciesColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor((row) => row.competencies ?? EMPTY_CAPPED_LIST, {
        id: 'competencies',
        cell: ({ getValue }) => {
            return <CappedItemsBadgeDropdown column="competencies" list={getValue()} />;
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="competencies" />,
    });
};

const getTaskDropdownTitle = (task: SkillCatalogTaskReference): string => `${task.title} · ${task.itemTitle}`;

type TasksColumnCallbacks = {
    readonly onItemClick: (item: SkillCatalogTaskReference) => void;
};

export const createTasksColumn = ({ onItemClick }: TasksColumnCallbacks): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor((row) => row.tasks ?? EMPTY_CAPPED_LIST, {
        id: 'tasks',
        cell: ({ getValue }) => {
            return (
                <CappedItemsBadgeDropdown
                    column="tasks"
                    getItemTitle={getTaskDropdownTitle}
                    list={getValue()}
                    onItemClick={onItemClick}
                />
            );
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="tasks" />,
    });
};

type SyncStatusDropdownProps = {
    readonly currentStatus: SkillSyncStatus;
    readonly onStatusChange: (status: SkillSyncStatus) => void;
};

const SyncStatusDropdown: FunctionComponent<SyncStatusDropdownProps> = ({ currentStatus, onStatusChange }) => {
    const translate = useTranslate();
    const statusLabel = translate(`pages.job-architecture-v2.skills.sync-status.${currentStatus}.title`);

    const handleStatusChange = (status: SkillSyncStatus): void => {
        if (status === currentStatus) return;

        onStatusChange(status);
    };

    return (
        <DropdownMenu>
            <BadgeDropdownTrigger
                accessibleName={translate('pages.job-architecture-v2.skills.sync-status-trigger', {
                    status: statusLabel,
                })}
            >
                <SyncStatusBadge currentStatus={currentStatus} interactive={true} />
            </BadgeDropdownTrigger>
            <DropdownMenuContent align="end" sideOffset={5} style={{ maxWidth: '250px' }}>
                <SyncStatusOptions currentStatus={currentStatus} onStatusChange={handleStatusChange} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

type SyncStatusColumnOptions = {
    readonly canManage: boolean;
    readonly onStatusChange: (skill: JobArchitectureSkill, status: SkillSyncStatus) => void;
};

export const createSyncStatusColumn = ({
    canManage,
    onStatusChange,
}: SyncStatusColumnOptions): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('syncStatus', {
        id: 'syncStatus',
        cell: ({ getValue, row }) => {
            const currentStatus = getValue();

            if (!canManage) {
                return <SyncStatusBadge currentStatus={currentStatus} />;
            }

            return (
                <SyncStatusDropdown
                    currentStatus={currentStatus}
                    onStatusChange={(status) => onStatusChange(row.original, status)}
                />
            );
        },
        enableSorting: false,
        header: () => <TranslatedHeader translationKey="sync-status" />,
    });
};

const AddedAtCell: FunctionComponent<{ readonly addedAt: string | undefined }> = memo(({ addedAt }) => {
    const {
        applicationState: { language },
    } = useContext(ApplicationContext);

    if (addedAt === undefined) {
        return <Fragment>–</Fragment>;
    }

    return <Fragment>{format(new Date(addedAt), 'PP', { locale: language.date.locale })}</Fragment>;
});

export const createAddedAtColumn = (): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.accessor('addedAt', {
        id: 'addedAt',
        cell: ({ getValue }) => {
            return <AddedAtCell addedAt={getValue()} />;
        },
        enableSorting: true,
        header: ({ column }) => <SortableTranslatedHeader column={column} translationKey="added-at" />,
        sortDescFirst: true,
    });
};

const ActionsCell = styled.div({
    display: 'flex',
    justifyContent: 'flex-end',
});

type SkillActionsDropdownProps = {
    readonly deleteDisabledReason: string | undefined;
    readonly onDelete: () => void;
    readonly onStatusChange: (status: SkillSyncStatus) => void;
    readonly renderExtraItems?: (skill: JobArchitectureSkill) => ReactNode;
    readonly skill: JobArchitectureSkill;
};

const SkillActionsDropdown: FunctionComponent<SkillActionsDropdownProps> = ({
    deleteDisabledReason,
    onDelete,
    onStatusChange,
    renderExtraItems,
    skill,
}) => {
    const { translate } = useContext(ApplicationContext);

    const handleStatusChange = (status: SkillSyncStatus): void => {
        if (status === skill.syncStatus) return;

        onStatusChange(status);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button
                    data-testid="skill-actions-button"
                    iconOnly={true}
                    size="sm"
                    variant="tertiary"
                    onClick={(e) => e.stopPropagation()}
                >
                    <IconHorizontalDots size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                        {translate('pages.job-architecture-v2.skills.change-sync-status')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent alignOffset={-5} sideOffset={2}>
                        <SyncStatusOptions currentStatus={skill.syncStatus} onStatusChange={handleStatusChange} />
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
                {renderExtraItems?.(skill)}
                <DropdownMenuItem
                    disabled={deleteDisabledReason !== undefined}
                    tooltipContent={deleteDisabledReason}
                    onSelect={onDelete}
                >
                    <IconDelete size={16} />
                    {translate('pages.job-architecture-v2.skills.delete.button')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export const createActionsColumn = ({
    getDeleteDisabledReason,
    hideableColumns = HIDEABLE_COLUMNS,
    onDelete,
    onStatusChange,
    renderExtraItems,
    translate,
}: {
    readonly getDeleteDisabledReason: (skill: JobArchitectureSkill) => string | undefined;
    readonly hideableColumns?: readonly TableConfigurableColumnDefinition<TranslationKey>[];
    readonly onDelete: (skill: JobArchitectureSkill) => void;
    readonly onStatusChange: (skill: JobArchitectureSkill, status: SkillSyncStatus) => void;
    readonly renderExtraItems?: (skill: JobArchitectureSkill) => ReactNode;
    readonly translate: Translate;
}): LegacyColumnDef<JobArchitectureSkill> => {
    return columnHelper.display({
        id: 'actions',
        cell: ({ row }) => {
            return (
                <ActionsCell>
                    <SkillActionsDropdown
                        deleteDisabledReason={getDeleteDisabledReason(row.original)}
                        renderExtraItems={renderExtraItems}
                        skill={row.original}
                        onDelete={() => onDelete(row.original)}
                        onStatusChange={(status) => onStatusChange(row.original, status)}
                    />
                </ActionsCell>
            );
        },
        enableHiding: false,
        enableSorting: false,
        header: ({ table }) => (
            <TableConfigureColumnsHeaderCell
                columnVisibility={table.store.state.columnVisibility}
                columns={hideableColumns}
                onColumnVisibilityChange={table.setColumnVisibility}
                translate={translate}
                triggerAriaLabel={translate('components.table.configure-columns')}
            />
        ),
    });
};
